import React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import {
  saveDraft,
  tryDraft,
  updateRoleChangeTriggerConfig,
} from "./reducer/actionCreators";
import { RootFormWithCommonParts } from "./RootFormWithCommonParts";
import { FormLabel } from "./FormLabel";
import { TriggerRootFormProps } from "./TriggerRootForm";
import { RoleChangeTriggerConfigs } from "@dots-projects/types";
import { useGuildRoles } from "./StepFormEditRoles";
import { Select, Spin } from "antd";
import produce from "immer";

export default function TriggerRootFormRoleChange({
  trigger,
}: TriggerRootFormProps) {
  // Like team_join, this invariant would break the page because the trigger.type would be
  // "button_click" not "role_change" due to createDraftTrigger.ts's getDraftTriggerData function
  // invariant(trigger.type === "role_change");

  const config = trigger.configs as RoleChangeTriggerConfigs;
  const defaultValues = {
    rolesAdded: config.rolesAdded?.map((roleId) => ({
      id: roleId,
    })),
    rolesRemoved: config.rolesRemoved?.map((roleId) => ({
      id: roleId,
    })),
  };

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { handleSubmit, control } = form;
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();

  const guildRoles = useGuildRoles(state.trigger.current["communityId"]);

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of the trigger (with updates)
    // without affecting the original trigger
    const newTrigger = produce(trigger, (triggerDraft) => {
      triggerDraft.configs = {
        ...triggerDraft.configs,
        rolesAdded: data.rolesAdded.map((role: { id: string }) => role.id),
        rolesRemoved: data.rolesRemoved.map((role: { id: string }) => role.id),
      };
    });
    dispatch(updateRoleChangeTriggerConfig({ newTrigger }));
    dispatch(saveDraft(onPersist));
  };

  const children: React.ReactNode[] = [];
  if (guildRoles.data) {
    for (let i = 0; i < guildRoles.data.length; i++) {
      // @everyone is not an actual role, so it's not shown as an option.
      if (guildRoles.data[i].name === "@everyone") {
        continue;
      }
      children.push(
        <Select.Option
          key={`${guildRoles.data[i].discord_id}`}
          value={guildRoles.data[i].discord_id}
          name={guildRoles.data[i].name}
        >
          {guildRoles.data[i].name}
        </Select.Option>
      );
    }
  }

  const { replace: replaceAddRoleIdField } = useFieldArray({
    control,
    name: "rolesAdded", // unique name for your Field Array
  });

  const { replace: replaceRemoveRoleIdField } = useFieldArray({
    control,
    name: "rolesRemoved", // unique name for your Field Array
  });

  const addRoleChange = (addRoles: string[]) => {
    replaceAddRoleIdField(addRoles.map((roleId) => ({ id: roleId })));
  };

  const removeRoleChange = (removeRoles: string[]) => {
    replaceRemoveRoleIdField(removeRoles.map((roleId) => ({ id: roleId })));
  };

  return (
    <RootFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      trigger={trigger}
    >
      {trigger.isDraft && (
        <button
          type="button"
          onClick={() => {
            dispatch({
              type: "show_modal",
              payload: { modalId: "try-draft" },
            });
            dispatch(tryDraft(onPersist));
          }}
          disabled={Boolean(state.editor.isPersisting)}
        >
          Click here to test
        </button>
      )}
      <FormLabel label="When the following roles are added">
        <Controller
          control={control}
          name="rolesAdded"
          render={({ field }) => {
            if (guildRoles.isLoading) {
              return <Spin />;
            } else {
              return (
                <Select
                  defaultValue={
                    field.value ? field.value.map((role) => role.id) : []
                  }
                  mode="multiple"
                  style={{ width: "100%" }}
                  allowClear
                  onChange={addRoleChange}
                  key={field.value?.toString()}
                  optionFilterProp="name"
                >
                  {children}
                </Select>
              );
            }
          }}
        />
      </FormLabel>
      <FormLabel label="When the following roles are removed">
        <Controller
          control={control}
          name="rolesRemoved"
          render={({ field }) => {
            if (guildRoles.isLoading) {
              return <Spin />;
            } else {
              return (
                <Select
                  defaultValue={
                    field.value ? field.value.map((role) => role.id) : []
                  }
                  mode="multiple"
                  style={{ width: "100%" }}
                  allowClear
                  onChange={removeRoleChange}
                  key={field.value?.toString()}
                >
                  {children}
                </Select>
              );
            }
          }}
        />
      </FormLabel>
    </RootFormWithCommonParts>
  );
}
